import React from 'react';

import { Skeleton } from 'src/components/Helpers/Skeleton';

import { isValidRange, generateValidLettersForRange, hasEnergyIconComponent } from './utils';

type DynamicEnergyIconProps = {
    range: string;
    letter: string;
    defaultRange?: string;
};

/**
 * Dynamic component that validates input and lazy-loads the appropriate energy icon
 *
 * This component handles the complete validation and rendering pipeline:
 * 1. Validates custom ranges using isValidRange() (skips validation for default range)
 * 2. Validates letters are within the specified range using generateValidLettersForRange()
 * 3. Checks component existence using hasEnergyIconComponent()
 * 4. Dynamically imports the appropriate EnergyIcon component
 * 5. Shows skeleton loading state during import
 * 6. Returns null for any validation failures or import errors
 *
 * @param range - The energy rating range (e.g., 'AG', 'AF')
 * @param letter - The specific energy rating letter (e.g., 'A', 'B', 'APlus')
 * @param defaultRange - Optional default range for validation optimization
 * @returns React component, Skeleton during loading, or null if validation fails
 */
export function DynamicEnergyIcon({ range, letter, defaultRange }: DynamicEnergyIconProps) {
    const [Component, setComponent] = React.useState<React.ComponentType | null>(null);
    const [loading, setLoading] = React.useState(true);

    // Validation logic - return null early if validation fails
    React.useEffect(() => {
        // Validate custom ranges (skip validation for default range)
        if (defaultRange && range !== defaultRange) {
            if (!isValidRange(range)) {
                setComponent(null);
                setLoading(false);
                return;
            }

            const validLetters = generateValidLettersForRange(range);

            if (!validLetters.includes(letter)) {
                setComponent(null);
                setLoading(false);
                return;
            }
        }

        // Check if component exists before attempting to load
        if (!hasEnergyIconComponent(range, letter)) {
            setComponent(null);
            setLoading(false);
            return;
        }

        // All validation passed, proceed with dynamic loading
        async function loadComponent() {
            try {
                const componentName = `EnergyIcon${range}${letter}`;
                let module;

                // Use global import function if available (for testing), otherwise use regular import
                if ((global as any).import) {
                    module = await (global as any).import(`./${range}/${componentName}`);
                } else {
                    module = await import(`./${range}/${componentName}`);
                }

                const ComponentToRender = module[componentName];

                if (ComponentToRender) {
                    setComponent(() => ComponentToRender);
                } else {
                    // Component not found, return null
                    setComponent(null);
                }
            } catch (error) {
                // Component doesn't exist, return null
                setComponent(null);
            } finally {
                setLoading(false);
            }
        }

        loadComponent();
    }, [range, letter, defaultRange]);

    if (loading) {
        return <Skeleton />;
    }

    if (!Component) {
        return null;
    }

    return <Component />;
}
